import React, { useContext, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  IconButton,
  Modal,
  TextField,
  Typography,
  useTheme,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Paper
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, SubmitHandler, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useUpdateCustomer } from "../../hooks/api/useCustomer";
import { Customer, CustomerUpdatesInput } from "./types/customer";
import { sanitizeRequestFalsyToNull } from "../../Utils/sanitizeRequestFalsyToNull";
import { CustomerType } from "./enums/customerType";
import { BRAND_COLOR, BRAND_HOVER } from "@kivunova/kivufrontendcommon";
import useMediaQuery from "@mui/material/useMediaQuery";
import AddressForm from "../common/AddressForm/AddressForm";
import {
  KivuI18nContext,
  PhoneCountryCodeMap,
  combinePhoneNumber,
  stringFormatter,
  useKivunovaTranslation,
  getDefaultCountryCode
} from "@kivunova/kivufrontendcommon";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { CustomerFormData } from "./types/customer";
import { buildCustomerSchema } from "../../Utils/customerSchema";

interface EditCustomerModalProps {
  open: boolean;
  onClose: () => void;
  customer: Customer;
}

const EditCustomerModal: React.FC<EditCustomerModalProps> = ({
  open,
  onClose,
  customer
}) => {
  const { t } = useKivunovaTranslation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { language, defaultLang } = useContext(KivuI18nContext) || {};
  const updateCustomer = useUpdateCustomer();
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));

  const userCompanyId = useSelector(
    (state: RootState) => state.user.userDetails?.companyId
  );
  const companyId = useSelector(
    (state: RootState) => state.profile.companyData?.id
  );
  const employeeCompanyId = useSelector(
    (state: RootState) => state.employees.employeeDetail?.companyId
  );
  const storeCompanyId = useSelector(
    (state: RootState) => state.store.storeDetails?.companyId
  );

  const resolvedCompanyId =
    companyId ?? userCompanyId ?? employeeCompanyId ?? storeCompanyId;

  // ✅ All hooks declared before any conditional return to satisfy Rules of Hooks.
  const customerSchema = useMemo(() => buildCustomerSchema(t), [t]);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch
  } = useForm<CustomerFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(customerSchema as any),
    mode: "onChange",
    defaultValues: {
      companyId: customer.companyId,
      name: customer.name,
      tin: customer.tin || "",
      phone: "",
      phoneCountryCode: getDefaultCountryCode(),
      email: customer.email || "",
      type: customer.type,
      address: customer.address || {
        country: "Rwanda",
        province: "",
        district: "",
        sector: "",
        cell: "",
        village: "",
        streetAddress: ""
      }
    }
  });

  const customerType = watch("type");

  // Helper to split a combined phone number back into country code + local number
  const extractPhoneComponents = (fullPhone: string) => {
    if (!fullPhone) {
      return { countryCode: getDefaultCountryCode(), phoneNumber: "" };
    }

    for (const [key, countryData] of Object.entries(PhoneCountryCodeMap)) {
      const code = countryData.code;
      if (fullPhone.startsWith(code)) {
        return {
          countryCode: key,
          phoneNumber: fullPhone.substring(code.length)
        };
      }
    }

    return { countryCode: getDefaultCountryCode(), phoneNumber: fullPhone };
  };

  useEffect(() => {
    if (open && customer) {
      const { countryCode, phoneNumber } = extractPhoneComponents(
        customer.phone ?? ""
      );

      reset({
        companyId: customer.companyId,
        name: customer.name,
        tin: customer.tin || "",
        phone: phoneNumber,
        phoneCountryCode: countryCode,
        email: customer.email || "",
        type: customer.type,
        address: customer.address || {
          country: "Rwanda",
          province: "",
          district: "",
          sector: "",
          cell: "",
          village: "",
          streetAddress: ""
        }
      });
    }
  }, [open, customer, reset]);

  // ✅ Conditional return is now safely after all hooks.
  if (!resolvedCompanyId) {
    console.error("❌ No companyId found for editing customer");
    return null;
  }

  const onSubmit: SubmitHandler<CustomerFormData> = async (data) => {
    try {
      const trimmedLocal = (data.phone ?? "").trim();
      const fullPhoneNumber = trimmedLocal
        ? combinePhoneNumber(
            data.phoneCountryCode as string,
            trimmedLocal,
            PhoneCountryCodeMap
          )
        : "";

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { phoneCountryCode, companyId, ...customerData } = data;

      const trimmedEmail = (customerData.email ?? "").trim();

      // Unlike create, PATCH must send phone/email when the user edits the form: omitted
      // keys mean "leave unchanged" on the server; empty string clears (see CustomerService).
      const updates = sanitizeRequestFalsyToNull({
        name: customerData.name,
        type: customerData.type,
        tin: customerData.tin,
        address: customerData.address,
        phone: fullPhoneNumber,
        email: trimmedEmail
      }) as unknown as CustomerUpdatesInput;

      await updateCustomer.mutateAsync({
        companyId: resolvedCompanyId,
        customerId: customer.id,
        updates
      });

      onClose();
    } catch (error) {
      console.error("Failed to update customer:", error);
    }
  };

  const isLoading = isSubmitting || updateCustomer.isPending;

  return (
    <Modal open={open} onClose={onClose}>
      <Box className="modal-styles">
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: matchMD ? 2 : 3,
            pb: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0
          }}
        >
          <Typography
            variant={matchMD ? "h6" : "h5"}
            fontWeight="bold"
            color="textPrimary"
          >
            {t("edit-customer", "Edit Customer")}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Scrollable Content Area */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: matchMD ? 2 : 3,
            pt: 2,
            display: "flex",
            flexDirection: "column",
            "&::-webkit-scrollbar": {
              width: "8px"
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f1f1f1",
              borderRadius: "10px"
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#888",
              borderRadius: "10px",
              "&:hover": {
                backgroundColor: "#555"
              }
            }
          }}
        >
          {/* Form - Vertical Layout */}
          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: matchMD ? 1.5 : 2
            }}
          >
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size={matchMD ? "small" : "medium"}
                  label={t("name", "Name")}
                  placeholder={t("enter-customer-name", "Enter customer name")}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  disabled={isLoading}
                />
              )}
            />

            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  size={matchMD ? "small" : "medium"}
                  label={t("customer-type", "Customer Type")}
                  error={!!errors.type}
                  helperText={errors.type?.message}
                  disabled={isLoading}
                >
                  {Object.values(CustomerType).map((type) => (
                    <MenuItem key={type} value={type}>
                      {stringFormatter(type)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            {customerType === CustomerType.COMPANY && (
              <Controller
                name="tin"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size={matchMD ? "small" : "medium"}
                    label={t("tin", "TIN (Tax Identification Number)")}
                    placeholder={t("enter-tin", "Enter TIN")}
                    error={!!errors.tin}
                    helperText={errors.tin?.message}
                    disabled={isLoading}
                  />
                )}
              />
            )}

            {/* Phone Number with Country Code */}
            <Box
              sx={{
                display: "flex",
                gap: matchMD ? 1 : 2,
                alignItems: "flex-start",
                flexDirection: matchMD ? "column" : "row"
              }}
            >
              <Controller
                name="phoneCountryCode"
                control={control}
                render={({ field }) => (
                  <FormControl
                    sx={{
                      flex: matchMD ? 1 : "0 0 160px",
                      minWidth: matchMD ? "100%" : 140,
                      width: matchMD ? "100%" : "auto"
                    }}
                    variant="outlined"
                    error={!!errors.phoneCountryCode}
                    disabled={isLoading}
                    size={matchMD ? "small" : "medium"}
                  >
                    <InputLabel id="phone-country-code-label">
                      {t("code", "Code")}
                    </InputLabel>
                    <Select
                      {...field}
                      value={field.value || getDefaultCountryCode()}
                      labelId="phone-country-code-label"
                      id="phone-country-code"
                      label={t("code", "Code")}
                      disabled={isLoading}
                    >
                      {Object.keys(PhoneCountryCodeMap).map((key) => (
                        <MenuItem key={key} value={key}>
                          {`${PhoneCountryCodeMap[key].flag} ${PhoneCountryCodeMap[key].code}`}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.phoneCountryCode && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5, ml: 1.75 }}
                      >
                        {errors.phoneCountryCode.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />

              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("phone-number", "Phone Number")}
                    fullWidth
                    size={matchMD ? "small" : "medium"}
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                    placeholder="782484464"
                    disabled={isLoading}
                  />
                )}
              />
            </Box>

            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size={matchMD ? "small" : "medium"}
                  label={t("email", "Email")}
                  placeholder={t("enter-email", "Enter email")}
                  type="email"
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  disabled={isLoading}
                />
              )}
            />

            {/* Address Section */}
            <Paper
              variant="outlined"
              sx={{
                p: matchMD ? 1.5 : 2,
                display: "flex",
                flexDirection: "column",
                gap: matchMD ? 1.5 : 2,
                backgroundColor: "background.paper"
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight={600}
                color="textPrimary"
              >
                {t("address", "Address")}
              </Typography>
              <AddressForm
                control={control}
                setValue={setValue}
                defaultAddress={useWatch({ control, name: "address" })}
              />
            </Paper>
          </Box>
        </Box>

        {/* Fixed Action Buttons */}
        <Box
          sx={{
            display: "flex",
            flexDirection: matchMD ? "column" : "row",
            gap: matchMD ? 1 : 2,
            p: matchMD ? 2 : 3,
            pt: matchMD ? 1.5 : 2,
            borderTop: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
            backgroundColor: "background.paper"
          }}
        >
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isLoading}
            onClick={handleSubmit(onSubmit)}
            fullWidth
            size={matchMD ? "medium" : "large"}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              backgroundColor: BRAND_COLOR,
              "&:hover": { backgroundColor: BRAND_HOVER }
            }}
          >
            {t("save-changes", "Save Changes")}
          </LoadingButton>

          <Button
            type="button"
            variant="outlined"
            onClick={onClose}
            disabled={isLoading}
            fullWidth
            size={matchMD ? "medium" : "large"}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              backgroundColor: "white",
              color: BRAND_COLOR,
              border: `1px solid ${BRAND_COLOR}`,
              "&:hover": {
                backgroundColor: BRAND_HOVER,
                color: "white",
                borderColor: BRAND_HOVER
              }
            }}
          >
            {t("cancel", "Cancel")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditCustomerModal;
